import type { FC } from 'react';
import Pain0 from '@/assets/icons/pain-scale/pain-0.svg?react';
import Pain1 from '@/assets/icons/pain-scale/pain-1.svg?react';
import Pain2 from '@/assets/icons/pain-scale/pain-2.svg?react';
import Pain3 from '@/assets/icons/pain-scale/pain-3.svg?react';
import Pain4 from '@/assets/icons/pain-scale/pain-4.svg?react';
import Pain5 from '@/assets/icons/pain-scale/pain-5.svg?react';
import Pain6 from '@/assets/icons/pain-scale/pain-6.svg?react';
import Pain7 from '@/assets/icons/pain-scale/pain-7.svg?react';
import Pain8 from '@/assets/icons/pain-scale/pain-8.svg?react';
import Pain9 from '@/assets/icons/pain-scale/pain-9.svg?react';
import Pain10 from '@/assets/icons/pain-scale/pain-10.svg?react';
import { useIds } from '@/hooks/use-ids';
import { cn } from '@/lib/utils';

const PAIN_SCALE_ITEMS = [
  { value: '0', icon: Pain0, color: 'text-sky-600', selectedBg: 'bg-sky-600' },
  {
    value: '1',
    icon: Pain1,
    color: 'text-green-700',
    selectedBg: 'bg-green-700',
  },
  {
    value: '2',
    icon: Pain2,
    color: 'text-green-600',
    selectedBg: 'bg-green-600',
  },
  {
    value: '3',
    icon: Pain3,
    color: 'text-lime-600',
    selectedBg: 'bg-lime-600',
  },
  {
    value: '4',
    icon: Pain4,
    color: 'text-yellow-500',
    selectedBg: 'bg-yellow-500',
  },
  {
    value: '5',
    icon: Pain5,
    color: 'text-amber-500',
    selectedBg: 'bg-amber-500',
  },
  {
    value: '6',
    icon: Pain6,
    color: 'text-amber-600',
    selectedBg: 'bg-amber-600',
  },
  {
    value: '7',
    icon: Pain7,
    color: 'text-orange-500',
    selectedBg: 'bg-orange-500',
  },
  {
    value: '8',
    icon: Pain8,
    color: 'text-orange-600',
    selectedBg: 'bg-orange-600',
  },
  { value: '9', icon: Pain9, color: 'text-red-600', selectedBg: 'bg-red-600' },
  {
    value: '10',
    icon: Pain10,
    color: 'text-red-900',
    selectedBg: 'bg-red-900',
  },
] as const;

const PAIN_SCALE_VALUES = PAIN_SCALE_ITEMS.map((item) => item.value);

type PainScaleProps = {
  name?: string;
  legend?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  hasError?: boolean;
  className?: string;
};

const PainScale: FC<PainScaleProps> = ({
  name,
  legend,
  value,
  onValueChange,
  disabled,
  hasError,
  className,
}) => {
  const [groupId] = useIds(1);
  const radioName = name ?? groupId;

  return (
    <div
      className={cn(
        'w-fit max-w-full overflow-x-auto overscroll-x-contain rounded-md border border-input shadow-xs select-none',
        hasError && 'border-destructive',
        className,
      )}
    >
      <fieldset
        aria-invalid={hasError || undefined}
        className="inline-flex w-max max-w-none"
        disabled={disabled}
      >
        <legend className="sr-only">{legend ?? 'Pain scale'}</legend>
        {PAIN_SCALE_ITEMS.map((item, index) => {
          const Icon = item.icon;
          const isSelected = value === item.value;
          const isLast = index === PAIN_SCALE_ITEMS.length - 1;
          const inputId = `${groupId}-pain-${item.value}`;

          return (
            /* oxlint-disable jsx-a11y/no-noninteractive-element-interactions -- label mousedown focuses associated radio without scrolling */
            <label
              className={cn(
                'inline-flex h-9 shrink-0 cursor-pointer items-center gap-2 bg-background px-2 transition-colors outline-none mobile:h-11',
                'hover:bg-accent has-focus-visible:z-10 has-focus-visible:ring-2 has-focus-visible:ring-ring/50',
                'has-disabled:pointer-events-none has-disabled:opacity-50',
                !isLast && 'border-r border-input',
                isSelected
                  ? [item.selectedBg, 'text-white hover:text-white']
                  : item.color,
              )}
              htmlFor={inputId}
              key={item.value}
              onMouseDown={(event) => {
                event.preventDefault();
                event.currentTarget.ownerDocument
                  .getElementById(inputId)
                  ?.focus({ preventScroll: true });
              }}
            >
              <input
                checked={isSelected}
                className="sr-only"
                disabled={disabled}
                id={inputId}
                name={radioName}
                // Prevent React controlled component warning; actual selection is handled by onClick
                onChange={() => {}}
                onClick={() => onValueChange?.(isSelected ? '' : item.value)}
                type="radio"
                value={item.value}
              />
              <Icon aria-hidden className="size-4" />
              <span className="text-sm">{item.value}</span>
            </label>
            /* oxlint-enable jsx-a11y/no-noninteractive-element-interactions */
          );
        })}
      </fieldset>
    </div>
  );
};

export { PAIN_SCALE_VALUES, PainScale };
